import React, { memo, useState, useEffect } from 'react';
import { View, Platform, Linking } from 'react-native';
import { QText, Button } from './index';
import { useTheme } from 'react-native-paper';
import { useGetAttachmentByIdQuery } from '../store/api/QueueApi';
import FileIcon from './Icons/FileIcon';
import ImageIcon from './Icons/ImageIcon';

/**
 * UniversalDocumentViewer component that handles multiple file types
 * Supports: PDF, Word, Excel, Text, CSV, Images
 * Uses browser for non-image files, embedded display for images
 */
const UniversalDocumentViewer = ({
  uri,
  attachmentId,
  fileType,
  fileName,
  style,
  onError,
}) => {
  const theme = useTheme();
  const [documentUri, setDocumentUri] = useState(uri);
  const [isLoading, setIsLoading] = useState(!uri);
  const [viewerType, setViewerType] = useState('unknown');

  // Use RTK Query to fetch the attachment by ID if no URI provided
  const {
    data: fileData,
    error,
    isLoading: isQueryLoading,
  } = useGetAttachmentByIdQuery(attachmentId, {
    skip: !!uri || !attachmentId,
  });

  // Determine file type and appropriate viewer
  useEffect(() => {
    const determineViewerType = () => {
      const type = fileType?.toLowerCase() || '';
      const name = fileName?.toLowerCase() || '';

      // Check by MIME type first
      if (type.includes('pdf') || name.endsWith('.pdf')) {
        return 'pdf';
      }
      if (
        type.includes('image/') ||
        /\.(jpg|jpeg|png|gif|svg|webp|bmp)$/i.test(name)
      ) {
        return 'image';
      }
      if (type.includes('text/plain') || name.endsWith('.txt')) {
        return 'text';
      }
      if (type.includes('text/csv') || name.endsWith('.csv')) {
        return 'csv';
      }
      if (
        type.includes('word') ||
        type.includes('document') ||
        /\.(doc|docx)$/i.test(name)
      ) {
        return 'word';
      }
      if (
        type.includes('excel') ||
        type.includes('sheet') ||
        /\.(xls|xlsx)$/i.test(name)
      ) {
        return 'excel';
      }

      return 'unknown';
    };

    setViewerType(determineViewerType());
  }, [fileType, fileName]);

  useEffect(() => {
    if (fileData && !uri) {
      try {
        // Convert the file data to a blob URL for display
        const binaryData = fileData.data || fileData;

        if (Array.isArray(binaryData)) {
          // Convert array of numbers to Uint8Array
          const uint8Array = new Uint8Array(binaryData);
          const blob = new Blob([uint8Array], {
            type: fileType || 'application/octet-stream',
          });
          const blobUrl = URL.createObjectURL(blob);
          setDocumentUri(blobUrl);
        } else if (binaryData instanceof Blob) {
          const blobUrl = URL.createObjectURL(binaryData);
          setDocumentUri(blobUrl);
        } else {
          console.error('Unexpected file data format:', typeof binaryData);
          if (onError) onError(new Error('Invalid file data format'));
        }
      } catch (err) {
        console.error('Error processing file data:', err);
        if (onError) onError(err);
      }
      setIsLoading(false);
    }
  }, [fileData, uri, fileType, onError]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (
        documentUri &&
        documentUri.startsWith('blob:') &&
        documentUri !== uri
      ) {
        URL.revokeObjectURL(documentUri);
      }
    };
  }, [documentUri, uri]);

  const handleOpenInBrowser = () => {
    if (documentUri) {
      Linking.openURL(documentUri).catch((err) => {
        console.error('Error opening document in browser:', err);
        if (onError) onError(err);
      });
    }
  };

  const handleDownload = () => {
    if (documentUri) {
      try {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = documentUri;

        // Always prioritize the original fileName prop if available
        let filename = 'document';

        if (fileName) {
          // Use the original filename if available (this preserves the original extension)
          filename = fileName;
        } else {
          // Fallback to generating filename based on file type and MIME type
          const getExtensionFromMimeType = (mimeType) => {
            switch (mimeType) {
              case 'image/jpeg':
                return '.jpg';
              case 'image/png':
                return '.png';
              case 'image/gif':
                return '.gif';
              case 'image/webp':
                return '.webp';
              case 'image/bmp':
                return '.bmp';
              case 'application/pdf':
                return '.pdf';
              case 'text/plain':
                return '.txt';
              case 'text/csv':
                return '.csv';
              case 'application/msword':
                return '.doc';
              case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return '.docx';
              case 'application/vnd.ms-excel':
                return '.xls';
              case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return '.xlsx';
              default:
                return '';
            }
          };

          const extension = getExtensionFromMimeType(fileType);
          switch (viewerType) {
            case 'pdf':
              filename = 'document.pdf';
              break;
            case 'text':
              filename = 'document.txt';
              break;
            case 'csv':
              filename = 'document.csv';
              break;
            case 'word':
              filename = 'document.docx';
              break;
            case 'excel':
              filename = 'document.xlsx';
              break;
            case 'image':
              // Use the correct extension based on MIME type
              filename = `image${extension || '.jpg'}`;
              break;
            default:
              filename = `document${extension}`;
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Error downloading document:', err);
        if (onError) onError(err);
      }
    }
  };

  if (isLoading || isQueryLoading) {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
          },
        ]}
      >
        <QText style={{ color: theme.colors.onSurface }}>
          Loading document...
        </QText>
      </View>
    );
  }

  if (error || !documentUri) {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
          },
        ]}
      >
        <QText style={{ color: theme.colors.error, textAlign: 'center' }}>
          Failed to load document: {error?.message || 'Unknown error'}
        </QText>
      </View>
    );
  }

  // Render based on viewer type
  const renderViewer = () => {
    switch (viewerType) {
      case 'pdf':
        return renderPDFViewer();
      case 'image':
        return renderImageViewer();
      case 'text':
        return renderTextViewer();
      case 'csv':
        return renderCSVViewer();
      case 'word':
        return renderWordViewer();
      case 'excel':
        return renderExcelViewer();
      default:
        return renderUnknownViewer();
    }
  };

  const renderPDFViewer = () => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          PDF Document Ready
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          Click the button below to open this PDF in your browser or default PDF
          viewer.
        </QText>
        <Button
          mode="contained"
          onPress={handleOpenInBrowser}
          style={{ marginBottom: 16 }}
        >
          Open PDF in Browser
        </Button>
        {Platform.OS === 'web' && (
          <QText
            style={{
              color: theme.colors.primary,
              textAlign: 'center',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            The PDF will open in a new tab
          </QText>
        )}
        <Button
          mode="outlined"
          onPress={handleDownload}
          style={{ marginBottom: 16 }}
        >
          Download
        </Button>
      </View>
    );
  };

  const renderImageViewer = () => {
    if (Platform.OS === 'web') {
      return (
        <View
          style={[
            style,
            {
              backgroundColor: theme.colors.surface,
              maxWidth: 600,
              alignSelf: 'center',
              width: '100%',
              padding: 20,
            },
          ]}
        >
          <img
            src={documentUri}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 8,
            }}
            alt={fileName || 'Image'}
            onError={(error) => {
              console.error('Image loading error:', error);
              if (onError) onError(error);
            }}
          />
          <View style={{ marginTop: 16, alignItems: 'center' }}>
            <Button
              mode="outlined"
              onPress={handleDownload}
              style={{ marginBottom: 16 }}
            >
              Download
            </Button>
          </View>
        </View>
      );
    } else {
      return renderMobileFallback('Image');
    }
  };

  const renderTextViewer = () => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          Text Document Ready
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          Click the button below to open this text document in your browser or
          text editor.
        </QText>
        <Button
          mode="contained"
          onPress={handleOpenInBrowser}
          style={{ marginBottom: 16 }}
        >
          Open Text Document
        </Button>
        {Platform.OS === 'web' && (
          <QText
            style={{
              color: theme.colors.primary,
              textAlign: 'center',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            The text file will open in a new tab
          </QText>
        )}
        <Button
          mode="outlined"
          onPress={handleDownload}
          style={{ marginBottom: 16 }}
        >
          Download
        </Button>
      </View>
    );
  };

  const renderCSVViewer = () => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          CSV Spreadsheet Ready
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          Click the button below to open this CSV file in your browser or
          spreadsheet application.
        </QText>
        <Button
          mode="contained"
          onPress={handleOpenInBrowser}
          style={{ marginBottom: 16 }}
        >
          Open CSV File
        </Button>
        {Platform.OS === 'web' && (
          <QText
            style={{
              color: theme.colors.primary,
              textAlign: 'center',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            The CSV will open in a new tab or download
          </QText>
        )}
        <Button
          mode="outlined"
          onPress={handleDownload}
          style={{ marginBottom: 16 }}
        >
          Download
        </Button>
      </View>
    );
  };

  const renderWordViewer = () => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          Word Document Ready
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          Click the button below to open this Word document in your browser or
          Microsoft Word.
        </QText>
        <Button
          mode="contained"
          onPress={handleOpenInBrowser}
          style={{ marginBottom: 16 }}
        >
          Open Word Document
        </Button>
        {Platform.OS === 'web' && (
          <QText
            style={{
              color: theme.colors.primary,
              textAlign: 'center',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            The document will open in a new tab or download
          </QText>
        )}
        <Button
          mode="outlined"
          onPress={handleDownload}
          style={{ marginBottom: 16 }}
        >
          Download
        </Button>
      </View>
    );
  };

  const renderExcelViewer = () => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          Excel Spreadsheet Ready
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          Click the button below to open this Excel spreadsheet in your browser
          or Microsoft Excel.
        </QText>
        <Button
          mode="contained"
          onPress={handleOpenInBrowser}
          style={{ marginBottom: 16 }}
        >
          Open Excel Spreadsheet
        </Button>
        {Platform.OS === 'web' && (
          <QText
            style={{
              color: theme.colors.primary,
              textAlign: 'center',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            The spreadsheet will open in a new tab or download
          </QText>
        )}
        <Button
          mode="outlined"
          onPress={handleDownload}
          style={{ marginBottom: 16 }}
        >
          Download
        </Button>
      </View>
    );
  };

  const renderUnknownViewer = () => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          Document Ready
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          Click the button below to open this document in your browser or
          default application.
        </QText>
        <Button
          mode="contained"
          onPress={handleOpenInBrowser}
          style={{ marginBottom: 16 }}
        >
          Open Document
        </Button>
        {Platform.OS === 'web' && (
          <QText
            style={{
              color: theme.colors.primary,
              textAlign: 'center',
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            The document will open in a new tab or download
          </QText>
        )}
        <Button
          mode="outlined"
          onPress={handleDownload}
          style={{ marginBottom: 16 }}
        >
          Download
        </Button>
      </View>
    );
  };

  const renderMobileFallback = (documentType) => {
    return (
      <View
        style={[
          style,
          {
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.surface,
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
            padding: 20,
          },
        ]}
      >
        <FileIcon width={64} height={64} color={theme.colors.primary} />
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          {documentType} Preview
        </QText>
        <QText
          style={{
            color: theme.colors.onSurface,
            textAlign: 'center',
            marginBottom: 16,
          }}
        >
          Preview is not available on mobile.
        </QText>
        <QText style={{ color: theme.colors.primary, textAlign: 'center' }}>
          Use the download button to open in your device's viewer.
        </QText>
      </View>
    );
  };

  return renderViewer();
};

export default memo(UniversalDocumentViewer);
